'use client';

import { usePathname } from 'next/navigation';
import React, { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { observabilityService } from '@/services/Metrics/ObservabilityService';
import { faroService } from '@/services/Metrics/FaroService';
import { rumService, type GameLifecycleEvent, type ConversionFunnelStep } from '@/services/Metrics/RUMService';
import { ga4Service } from '@/services/Metrics/GA4Service';
import { FaroReactIntegration, FaroErrorBoundary } from '@/components/FaroReactIntegration';

interface ObservabilityContextType {
  reportData: typeof observabilityService.reportData;
  setUserContext: typeof observabilityService.setUserContext;
  trackBusinessEvent: typeof observabilityService.trackBusinessEvent;
  trackPageView: typeof rumService.trackPageView;
  trackGameLifecycle: typeof rumService.trackGameLifecycle;
  trackConversionStep: typeof rumService.trackConversionStep;
  trackFeatureUsage: typeof rumService.trackFeatureUsage;
  trackErrorRecovery: typeof rumService.trackErrorRecovery;
  isEnabled: boolean;
  isInitialized: boolean;
}

const ObservabilityContext = createContext<ObservabilityContextType | null>(null);

interface ObservabilityProviderProps {
  children: ReactNode;
  enableErrorBoundary?: boolean;
  errorFallback?: React.ComponentType<{ error: Error }>;
}

export const ObservabilityProvider: React.FC<ObservabilityProviderProps> = ({
  children,
  enableErrorBoundary = true,
  errorFallback
}) => {
  const isDirectTv = usePathname() === '/tv/direct';
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize services on mount
  useEffect(() => {
    if (isDirectTv) return;
    // Initialize asynchronously to prevent blocking layout rendering
    const initializeServices = async () => {
      try {
        // Initialize Faro SDK (React-optimized) - centralized configuration
        await faroService.initialize();
        
        // Initialize RUM service for comprehensive user monitoring
        rumService.initialize();
        
        // Mark as initialized
        setIsInitialized(true);
        console.log('[ObservabilityProvider] Observability services initialized successfully');
      } catch (error) {
        console.error('[ObservabilityProvider] Failed to initialize observability system:', error);
        // Still mark as initialized to prevent infinite loops, but services may not work
        setIsInitialized(true);
      }
    };

    // Run initialization without blocking
    initializeServices();
  }, [isDirectTv]);

  // Memoize bound functions to prevent new references on every render
  const stableFunctions = useMemo(() => ({
    reportData: observabilityService.reportData.bind(observabilityService),
    setUserContext: (...args: Parameters<typeof observabilityService.setUserContext>) => {
      observabilityService.setUserContext(...args);
      // Link user identity in GA4 for cross-session analysis
      if (args[0]) ga4Service.setUserProperties(args[0]);
    },
    trackBusinessEvent: observabilityService.trackBusinessEvent.bind(observabilityService),
    trackPageView: rumService.trackPageView.bind(rumService),
    trackGameLifecycle: rumService.trackGameLifecycle.bind(rumService),
    trackConversionStep: rumService.trackConversionStep.bind(rumService),
    trackFeatureUsage: rumService.trackFeatureUsage.bind(rumService),
    trackErrorRecovery: rumService.trackErrorRecovery.bind(rumService),
    isEnabled: observabilityService.getConfig().enabled,
  }), []); // Empty deps - services are singletons, methods don't change

  const contextValue: ObservabilityContextType = useMemo(() => ({
    ...stableFunctions,
    isInitialized,
  }), [stableFunctions, isInitialized]);

  // Always render children - don't block layout rendering during initialization
  // The services will initialize in the background
  // if (!isInitialized) {
  //   return null; // This was causing the blank page issue
  // }

  const content = (
    <ObservabilityContext.Provider value={contextValue}>
      {isDirectTv ? children : <FaroReactIntegration>{children}</FaroReactIntegration>}
    </ObservabilityContext.Provider>
  );

  // Wrap with error boundary if enabled
  if (enableErrorBoundary) {
    return (
      <FaroErrorBoundary fallback={errorFallback}>
        {content}
      </FaroErrorBoundary>
    );
  }

  return content;
};

export const useObservability = (): ObservabilityContextType => {
  const context = useContext(ObservabilityContext);
  if (!context) {
    throw new Error('useObservability must be used within an ObservabilityProvider');
  }
  return context;
};

// Export convenience hooks
export const useTrackBusinessEvent = () => {
  const { trackBusinessEvent } = useObservability();
  return trackBusinessEvent;
};

export const useSetUserContext = () => {
  const { setUserContext } = useObservability();
  return setUserContext;
};

// Export RUM-specific hooks
export const useTrackPageView = () => {
  const { trackPageView } = useObservability();
  return trackPageView;
};

export const useTrackGameLifecycle = () => {
  const { trackGameLifecycle } = useObservability();
  return trackGameLifecycle;
};

export const useTrackConversionStep = () => {
  const { trackConversionStep } = useObservability();
  return trackConversionStep;
};

export const useTrackFeatureUsage = () => {
  const { trackFeatureUsage } = useObservability();
  return trackFeatureUsage;
};

export const useTrackErrorRecovery = () => {
  const { trackErrorRecovery } = useObservability();
  return trackErrorRecovery;
};

// Export types for external use
export type { GameLifecycleEvent, ConversionFunnelStep };
